import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { adminApi } from '../services/adminApi';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

interface ImpersonatedTenant {
  id: string;
  name: string;
  domain: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedTenant: ImpersonatedTenant | null;
  sessionId: string | null;
  startedAt: string | null;
  actionsCount: number;
  startImpersonation: (tenantId: string, reason?: string) => Promise<boolean>;
  endImpersonation: () => Promise<boolean>;
  logAction: (actionType: 'create' | 'update' | 'delete' | 'view' | 'export', resourceType: string, resourceId?: string, details?: Record<string, unknown>) => Promise<void>;
  isLoading: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider = ({ children }: ImpersonationProviderProps) => {
  const { user } = useAuth();
  const { setSelectedTenantId } = useTenant();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedTenant, setImpersonatedTenant] = useState<ImpersonatedTenant | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [actionsCount, setActionsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkActiveSession = async () => {
      if (!user?.isMegaAdmin) {
        setIsImpersonating(false);
        setImpersonatedTenant(null);
        setSessionId(null);
        return;
      }

      try {
        const session = await adminApi.getActiveImpersonation();
        if (session.active && session.tenant) {
          setIsImpersonating(true);
          setImpersonatedTenant(session.tenant);
          setSessionId(session.session_id || null);
          setStartedAt(session.started_at || null);
          setActionsCount(session.actions_count || 0);
          setSelectedTenantId(session.tenant.id);
        }
      } catch (error) {
        console.error('Error checking impersonation session:', error);
      }
    };

    checkActiveSession();
  }, [user?.isMegaAdmin, setSelectedTenantId]);

  const startImpersonation = useCallback(async (tenantId: string, reason?: string): Promise<boolean> => {
    if (!user?.isMegaAdmin) {
      console.error('Only Mega Admins can impersonate tenants');
      return false;
    }

    setIsLoading(true);
    try {
      const result = await adminApi.startImpersonation(tenantId, reason);
      if (result.success && result.tenant) {
        setIsImpersonating(true);
        setImpersonatedTenant(result.tenant);
        setSessionId(result.session_id || null);
        setStartedAt(new Date().toISOString());
        setActionsCount(0);
        setSelectedTenantId(tenantId);
        return true;
      }
      console.error('Failed to start impersonation:', result.error);
      return false;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user?.isMegaAdmin, setSelectedTenantId]);

  const endImpersonation = useCallback(async (): Promise<boolean> => {
    if (!isImpersonating) {
      return true;
    }

    setIsLoading(true);
    try {
      const result = await adminApi.endImpersonation(sessionId || undefined);
      if (result.success) {
        setIsImpersonating(false);
        setImpersonatedTenant(null);
        setSessionId(null);
        setStartedAt(null);
        setActionsCount(0);
        setSelectedTenantId(null);
        return true;
      }
      console.error('Failed to end impersonation:', result.error);
      return false;
    } catch (error) {
      console.error('Error ending impersonation:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isImpersonating, sessionId, setSelectedTenantId]);

  const logAction = useCallback(async (
    actionType: 'create' | 'update' | 'delete' | 'view' | 'export',
    resourceType: string,
    resourceId?: string,
    details?: Record<string, unknown>
  ): Promise<void> => {
    if (!isImpersonating) return;

    try {
      const result = await adminApi.logImpersonationAction(
        actionType,
        resourceType,
        resourceId,
        details
      );
      if (result.logged) {
        setActionsCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error logging impersonation action:', error);
    }
  }, [isImpersonating]);

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating,
      impersonatedTenant,
      sessionId,
      startedAt,
      actionsCount,
      startImpersonation,
      endImpersonation,
      logAction,
      isLoading
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};
